import { css } from "glamor";
import React from "react";
import { colors, spacing } from "../library/theme";
import { formatDuration } from "../../utils/studyPlanFormatters";

const VISUAL = {
  completed: "completed",
  current: "current",
  upcoming: "upcoming",
};

// ── row containers ──────────────────────────────────────────────────────────

const rowBase = {
  display: "flex",
  alignItems: "center",
  gap: spacing.s,
  padding: `${spacing.s} ${spacing.m}`,
  borderRadius: 10,
  cursor: "pointer",
  transition: "background 150ms ease",
};

const completedRowClass = css({
  ...rowBase,
  ":hover": { backgroundColor: `${colors.greyLighter}` },
});

const currentRowClass = css({
  ...rowBase,
  backgroundColor: "#EAF6FF",
  border: `1px solid ${colors.primaryClear}`,
  padding: `${spacing.m} ${spacing.m}`,
  ":hover": { backgroundColor: "#DDEFFE" },
});

const upcomingRowClass = css({
  ...rowBase,
  ":hover": { backgroundColor: `${colors.greyLighter}` },
});

// ── status indicator (left circle / dot) ────────────────────────────────────

const circleBase = {
  width: 24,
  height: 24,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const completedCircleClass = css({
  ...circleBase,
  backgroundColor: "#E8F5E9",
  color: "#43A047",
  fontSize: 14,
});

const currentDotClass = css({
  ...circleBase,
  width: 12,
  height: 12,
  backgroundColor: colors.primary,
  marginLeft: 6,
  marginRight: 6,
});

const upcomingCircleClass = css({
  ...circleBase,
  border: `2px solid ${colors.greyLight}`,
  backgroundColor: "transparent",
});

// ── text area ───────────────────────────────────────────────────────────────

const textAreaClass = css({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

const lessonNameBase = {
  fontSize: 14,
  lineHeight: 1.4,
  margin: 0,
};

const completedNameClass = css({
  ...lessonNameBase,
  color: colors.greyDark,
  textDecoration: "line-through",
  fontWeight: 400,
});

const currentNameClass = css({
  ...lessonNameBase,
  color: colors.greyShadow,
  fontWeight: 600,
});

const upcomingNameClass = css({
  ...lessonNameBase,
  color: colors.greyDarker,
  fontWeight: 500,
});

// ── right side (duration / action) ──────────────────────────────────────────

const rightSideClass = css({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
  flexShrink: 0,
});

const durationPillClass = css({
  fontSize: 12,
  fontWeight: 500,
  color: colors.greyDarker,
  backgroundColor: colors.greyLighter,
  borderRadius: 6,
  padding: "2px 8px",
});

const currentDurationClass = css({
  fontSize: 13,
  fontWeight: 600,
  color: colors.greyShadow,
});

const reviewBtnClass = css({
  fontSize: 12,
  fontWeight: 600,
  color: colors.primary,
  cursor: "pointer",
  padding: "4px 10px",
  borderRadius: 6,
  border: `1px solid ${colors.primaryClear}`,
  backgroundColor: "transparent",
  transition: "all 150ms ease",
  ":hover": {
    backgroundColor: "#EAF6FF",
  },
});

const playBtnLargeClass = css({
  width: 36,
  height: 36,
  borderRadius: "50%",
  backgroundColor: colors.primary,
  color: colors.white,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  fontSize: 16,
  cursor: "pointer",
  transition: "background 150ms ease",
  ":hover": { backgroundColor: colors.primaryDark },
});

const playBtnSmallClass = css({
  width: 28,
  height: 28,
  borderRadius: "50%",
  backgroundColor: colors.greyLighter,
  color: colors.greyDark,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  fontSize: 13,
  cursor: "pointer",
  transition: "background 150ms ease",
  ":hover": {
    backgroundColor: colors.greyLight,
    color: colors.greyShadow,
  },
});

// ── tag "A SEGUIR" ──────────────────────────────────────────────────────────

const nextTagClass = css({
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: "#43A047",
  backgroundColor: "#E8F5E9",
  borderRadius: 4,
  padding: "2px 8px",
  flexShrink: 0,
});

const completedDateStyle = {
  fontSize: 11,
  color: colors.greyDark,
  margin: 0,
};

// ── helpers ─────────────────────────────────────────────────────────────────

function formatCompletedDate(isoString) {
  if (!isoString) return null;
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

// ── component ───────────────────────────────────────────────────────────────

export default function StudyPlanLessonRow({
  lesson,
  visualStatus,
  onPlay,
  isToday,
  showReviewButton = true,
}) {
  const isCurrent = visualStatus === VISUAL.current;
  const isCompleted = visualStatus === VISUAL.completed;
  const showCurrentHighlight = isCurrent && isToday;

  const rowClass = isCompleted
    ? completedRowClass
    : showCurrentHighlight
    ? currentRowClass
    : upcomingRowClass;

  const circleEl = isCompleted ? (
    <div className={completedCircleClass}>
      <i className="check icon" style={{ margin: 0, lineHeight: 1 }} />
    </div>
  ) : showCurrentHighlight ? (
    <div className={currentDotClass} />
  ) : (
    <div className={upcomingCircleClass} />
  );

  const nameClass = isCompleted
    ? completedNameClass
    : showCurrentHighlight
    ? currentNameClass
    : upcomingNameClass;

  return (
    <div className={rowClass} onClick={() => onPlay && onPlay(lesson)}>
      {circleEl}

      <div className={textAreaClass}>
        <p className={nameClass}>{lesson.name}</p>
        {isCompleted && lesson.completedAt && (
          <p style={completedDateStyle}>
            Assistida em {formatCompletedDate(lesson.completedAt)}
          </p>
        )}
      </div>

      <div className={rightSideClass}>
        {showCurrentHighlight && <span className={nextTagClass}>A SEGUIR</span>}

        {isCompleted && showReviewButton ? (
          <button
            className={reviewBtnClass}
            onClick={(e) => {
              e.stopPropagation();
              onPlay && onPlay(lesson);
            }}
          >
            Rever
          </button>
        ) : isCompleted ? null : showCurrentHighlight ? (
          <>
            <span className={currentDurationClass}>
              {formatDuration(lesson.durationSeconds)}
            </span>
            <div
              className={playBtnLargeClass}
              onClick={(e) => {
                e.stopPropagation();
                onPlay && onPlay(lesson);
              }}
            >
              <i
                className="play icon"
                style={{ margin: 0, lineHeight: 1, marginLeft: 2 }}
              />
            </div>
          </>
        ) : (
          <>
            <span className={durationPillClass}>
              {formatDuration(lesson.durationSeconds)}
            </span>
            <div
              className={playBtnSmallClass}
              onClick={(e) => {
                e.stopPropagation();
                onPlay && onPlay(lesson);
              }}
            >
              <i
                className="play icon"
                style={{ margin: 0, lineHeight: 1, marginLeft: 1 }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
